import React from 'react';
import {
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  FormControl,
  Select,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import {
  FormInputLabel,
  MenuProps,
  SelectedItemChip,
  useStyles
} from './styled';
import Icons from '../icons';
import pallet from '../../layouts/pallet';
import StyledCheckbox from '../../modules/common/styled/Checkbox';
import { fontSizes } from '../../layouts/fontSizes';
import { OptionsProps } from './types';
import { SxProps } from '@mui/system';
import Placeholder from '../Placeholder/Placeholder';

interface MultiSelectWithAllProps {
  label: string;
  options: OptionsProps[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  hasTopLabel?: boolean;
  required?: boolean;
  formControlMargin?: 'normal' | 'none' | 'dense' | undefined;
  sx?: SxProps;
  placeholder?: string;
}

const MultiSelectWithAll = ({
  label,
  options,
  selected,
  onChange,
  disabled,
  hasTopLabel = true,
  required = true,
  sx,
  placeholder
}: MultiSelectWithAllProps) => {
  const classes = useStyles();

  const allValues = options.map((option) => option.value);
  const selectedSet = new Set(selected);
  const isAllSelected = selected.length === allValues.length;
  const isIndeterminate = selected.length > 0 && !isAllSelected;

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: string[]; name?: string } }),
    _child?: React.ReactNode
  ) => {
    const value = event.target.value as string[];

    const lastSelected = value[value.length - 1];

    if (lastSelected === 'all') {
      const shouldDeselectAll = selected.length === allValues.length;
      onChange(shouldDeselectAll ? [] : allValues);
    } else {
      onChange(value);
    }
  };

  const handleDelete = (valueToDelete: string) => {
    const newSelected = selected.filter((value) => value !== valueToDelete);
    onChange(newSelected);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        ...sx
      }}
    >
      {hasTopLabel && (
        <InputLabel
          required={required}
          sx={{
            mb: '6px',
            color: pallet.textLabel,
            fontSize: `${fontSizes.placeHolder} !important`,
            fontWeight: 600
          }}
        >
          {label}
        </InputLabel>
      )}
      <FormControl
        fullWidth
        variant='outlined'
        sx={{ position: 'relative', m: 0, '&.MuiFormControl-root': { m: 0 } }}
      >
        {!hasTopLabel && <FormInputLabel>{label}</FormInputLabel>}
        <Select
          multiple
          value={selected}
          onChange={handleChange}
          disabled={disabled}
          MenuProps={{
            ...MenuProps,
            PaperProps: {
              ...(MenuProps as any)?.PaperProps,
              style: {
                maxHeight: 320,
                overflowY: 'auto'
              }
            }
          }}
          notched={!hasTopLabel}
          displayEmpty
          IconComponent={Icons.CaretDown}
          sx={{
            '& svg': {
              fill: pallet.formLabelText,
              color: pallet.formLabelText
            }
          }}
          renderValue={(selectedValues) => {
            const values = selectedValues as string[];
            if (!values || values.length === 0) {
              return <Placeholder text={placeholder ?? ''} />;
            }
            return (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' }
                }}
              >
                {values.map((value) => {
                  const option = options.find((opt) => opt.value === value);
                  return (
                    <SelectedItemChip key={value}>
                      <Typography>{option ? option.label : value}</Typography>
                      <IconButton
                        onClick={
                          disabled ? undefined : () => handleDelete(value)
                        }
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        size='small'
                        style={{
                          marginLeft: 0.5,
                          padding: '2px',
                          color: disabled ? pallet.lightGrey : pallet.textGrey
                        }}
                        disabled={disabled}
                      >
                        <Icons.CloseCircleFilled />
                      </IconButton>
                    </SelectedItemChip>
                  );
                })}
              </Box>
            );
          }}
        >
          <MenuItem
            value='all'
            classes={{ root: isAllSelected ? classes.selectedAll : '' }}
          >
            <ListItemIcon>
              <StyledCheckbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                classes={{ indeterminate: classes.indeterminateColor }}
              />
            </ListItemIcon>
            <ListItemText
              primary='Select All'
              classes={{ primary: classes.selectAllText }}
            />
          </MenuItem>

          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <ListItemIcon>
                <StyledCheckbox checked={selectedSet.has(option.value)} />
              </ListItemIcon>
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

MultiSelectWithAll.defaultProps = {
  disabled: false,
};

export default MultiSelectWithAll;
